import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import APIs from '../../../../../application/APIs';
import { Placeholder, PlaceholderStyle, FriendlyColumn } from './types';
import { usePDFInfo } from './usePDFInfo';
import { usePlaceholders } from './usePlaceholders';
import { usePopupState } from './usePopupState';
import { useDBColumns } from './useDBColumns';
import { useAutoFill } from './useAutoFill';

const DEFAULT_STYLE: PlaceholderStyle = {
  font_size: 12,
  font_name: 'Helvetica',
  color: '#000000',
};

interface UsePDFEditorProps {
  templateId: string;
}

export const usePDFEditor = ({ templateId }: UsePDFEditorProps) => {
  const dbColumns = useDBColumns();

  const placeholders = usePlaceholders({ templateId });

  // Callback for when initial data is loaded (PDF info + columns + placeholders)
  const handleDataLoaded = useCallback((data: {
    placeholders: Placeholder[];
    dbColumns: string[];
    friendlyColumns: FriendlyColumn[];
  }) => {
    placeholders.setPlaceholders(data.placeholders);
    dbColumns.setColumns(data.dbColumns, data.friendlyColumns);
  // Stable: setPlaceholders and setColumns are useState setters that never change identity
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pdfInfo = usePDFInfo({ templateId, onDataLoaded: handleDataLoaded });

  // Ref to always access latest currentPage for async callbacks
  const currentPageRef = useRef(pdfInfo.currentPage);
  currentPageRef.current = pdfInfo.currentPage;

  const getCurrentPage = useCallback(() => currentPageRef.current, []);

  // When popup creates a placeholder, add it and select it
  const handlePlaceholderCreated = useCallback((placeholder: Placeholder) => {
    placeholders.addPlaceholderAndSelect(placeholder);
  // Stable: addPlaceholderAndSelect wraps a useState setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const popup = usePopupState({
    templateId,
    getCurrentPage,
    onPlaceholderCreated: handlePlaceholderCreated,
  });

  // Getter for placeholders ref (stable callback for useAutoFill)
  const getPlaceholders = useCallback(() => placeholders.placeholdersRef.current, [placeholders.placeholdersRef]);

  const autoFill = useAutoFill({
    getPlaceholders,
    updatePlaceholdersValues: placeholders.updatePlaceholdersValues,
  });

  // Wrap setCurrentPage to also clear placeholder selection (original behavior)
  const setCurrentPage = useCallback((page: number) => {
    pdfInfo.setCurrentPage(page);
    placeholders.clearSelection();
  // Stable: setCurrentPage and clearSelection are useState setters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wrap getCurrentPagePlaceholders to use current page from pdfInfo
  const getCurrentPagePlaceholders = useCallback(() => {
    return placeholders.getCurrentPagePlaceholders(pdfInfo.currentPage);
  }, [placeholders, pdfInfo.currentPage]);

  // Legacy finishDrawing: bridges drawing state (from usePlaceholders) with currentPage (from usePDFInfo)
  const finishDrawing = useCallback(async (dbColumn: string) => {
    const drawingState = placeholders.stateRef.current;
    if (!drawingState.isDrawing || !drawingState.drawStart || !drawingState.drawCurrent) {
      placeholders.cancelDrawing();
      return;
    }

    const minX = Math.min(drawingState.drawStart.x, drawingState.drawCurrent.x);
    const minY = Math.min(drawingState.drawStart.y, drawingState.drawCurrent.y);
    const w = Math.abs(drawingState.drawCurrent.x - drawingState.drawStart.x);
    const h = Math.abs(drawingState.drawCurrent.y - drawingState.drawStart.y);

    // Minimum size check
    if (w < 20 || h < 10) {
      placeholders.cancelDrawing();
      return;
    }

    // Detect font at the rectangle location
    let detectedStyle: PlaceholderStyle = { ...DEFAULT_STYLE };
    try {
      const fontInfo = await APIs.detectFontAtRect(templateId, currentPageRef.current, { x: minX, y: minY, w, h });
      detectedStyle = {
        font_size: fontInfo.font_size,
        font_name: fontInfo.font_name,
        align: 'left',
        color: fontInfo.color,
      };
    } catch (error) {
      console.error('Failed to detect font, using defaults:', error);
    }

    const newPlaceholder: Placeholder = {
      id: uuidv4(),
      type: 'placeholder',
      db_column: dbColumn,
      page: currentPageRef.current,
      rect: { x: minX, y: minY, w, h },
      style: detectedStyle,
    };

    placeholders.cancelDrawing();
    placeholders.addPlaceholderAndSelect(newPlaceholder);
  // Only depends on templateId; other refs accessed via .current at call time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Return flattened API for backward compatibility
  return {
    // PDF info state
    pageCount: pdfInfo.pageCount,
    pageSizes: pdfInfo.pageSizes,
    currentPage: pdfInfo.currentPage,
    scale: pdfInfo.scale,
    loading: pdfInfo.loading,
    error: pdfInfo.error,
    setCurrentPage,
    setScale: pdfInfo.setScale,

    // Placeholders state
    placeholders: placeholders.placeholders,
    selectedPlaceholderId: placeholders.selectedPlaceholderId,
    saving: placeholders.saving,
    isDrawing: placeholders.isDrawing,
    drawStart: placeholders.drawStart,
    drawCurrent: placeholders.drawCurrent,
    addPlaceholder: placeholders.addPlaceholder,
    updatePlaceholder: placeholders.updatePlaceholder,
    deletePlaceholder: placeholders.deletePlaceholder,
    selectPlaceholder: placeholders.selectPlaceholder,
    savePlaceholders: placeholders.savePlaceholders,
    getCurrentPagePlaceholders,
    getSelectedPlaceholder: placeholders.getSelectedPlaceholder,
    // Legacy drawing
    startDrawing: placeholders.startDrawing,
    updateDrawing: placeholders.updateDrawing,
    finishDrawing,
    cancelDrawing: placeholders.cancelDrawing,
    getDrawingRect: placeholders.getDrawingRect,

    // Popup state
    popupState: popup.popupState,
    openPopup: popup.openPopup,
    closePopup: popup.closePopup,
    setPopupMode: popup.setPopupMode,
    selectColumnInPopup: popup.selectColumnInPopup,
    createStaticTextInPopup: popup.createStaticTextInPopup,
    createCustomVariableInPopup: popup.createCustomVariableInPopup,
    goBackToColumns: popup.goBackToColumns,
    createPlaceholderWithValue: popup.createPlaceholderWithValue,

    // DB columns
    dbColumns: dbColumns.dbColumns,
    friendlyColumns: dbColumns.friendlyColumns,
    getGroupedFriendlyColumns: dbColumns.getGroupedFriendlyColumns,

    // Auto-fill
    selectedClientId: autoFill.selectedClientId,
    selectedBankAccountId: autoFill.selectedBankAccountId,
    setSelectedClientId: autoFill.setSelectedClientId,
    setSelectedBankAccountId: autoFill.setSelectedBankAccountId,
    autoFillPlaceholders: autoFill.autoFillPlaceholders,
    getFieldValues: autoFill.getFieldValues,
    getRequiredCollections: autoFill.getRequiredCollections,
  };
};
